import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { previewWhatsNew } from '../components/changelog/WhatsNewModalController';

export const Settings: React.FC = () => {
  const { user, checkAuthStatus } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);
    
    try {
      await api.put(`/auth/users/${user?.id}`, {
        full_name: fullName,
      });
      
      // Refresh user data
      await checkAuthStatus();
      
      setMessage({
        type: 'success',
        text: 'Your profile settings have been updated successfully.'
      });
    } catch (error) {
      console.error('Failed to update settings:', error);
      setMessage({
        type: 'error',
        text: 'There was a problem updating your settings. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!user) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-5xl mx-auto">
      <h1 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
        Settings
      </h1>
      
      {message && (
        <div className={`p-4 mb-6 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
            : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
        }`}>
          {message.text}
        </div>
      )}
      
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 mb-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Appearance
        </h2>
        
        <div className="flex items-center justify-between">
          <span className={darkMode ? 'text-white' : 'text-gray-800'}>Dark Mode</span>
          <button
            onClick={toggleDarkMode}
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              darkMode ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                darkMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
      
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 mb-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Profile Settings
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              className={`w-full px-3 py-2 border rounded-md ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-gray-100 border-gray-300 text-gray-900'
              }`}
            />
            <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Email cannot be changed
            </p>
          </div>
          
          <div className="mb-4">
            <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400`}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
      {/* Developer Tools */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Developer Tools
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Preview What's New Modal
              </p>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Clears the "last seen" flag and reloads to re-trigger the release notes modal.
              </p>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('whatsNewLastSeen');
                previewWhatsNew();
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                darkMode
                  ? 'bg-indigo-700 hover:bg-indigo-600 text-white'
                  : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800'
              }`}
            >
              Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
